import React, { useState, useRef } from "react";
import { Play, Pause, Video, Mic, Type, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface IntroCapsuleViewerProps {
  open: boolean;
  onClose: () => void;
  introType: "video" | "voice" | "text";
  introContentUrl?: string | null;
  introText?: string | null;
  senderName: string;
}

const IntroCapsuleViewer = ({
  open,
  onClose,
  introType,
  introContentUrl,
  introText,
  senderName,
}: IntroCapsuleViewerProps) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);

  const togglePlayVideo = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const togglePlayAudio = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleVideoEnded = () => setIsPlaying(false);
  const handleAudioEnded = () => setIsPlaying(false);

  const getTypeIcon = () => {
    switch (introType) {
      case "video":
        return <Video className="w-5 h-5 text-rose-500" />;
      case "voice":
        return <Mic className="w-5 h-5 text-violet-500" />;
      case "text":
        return <Type className="w-5 h-5 text-blue-500" />;
    }
  };

  const getTypeLabel = () => {
    switch (introType) {
      case "video":
        return "Video Intro";
      case "voice":
        return "Voice Intro";
      case "text":
        return "Text Intro";
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {getTypeIcon()}
            {senderName}'s {getTypeLabel()}
          </DialogTitle>
        </DialogHeader>

        <div className="py-4">
          {introType === "video" && introContentUrl && (
            <div className="relative aspect-[9/16] max-h-80 bg-black rounded-xl overflow-hidden">
              <video
                ref={videoRef}
                src={introContentUrl}
                className="w-full h-full object-cover"
                playsInline
                onEnded={handleVideoEnded}
              />
              
              {/* Play button overlay */}
              <button
                onClick={togglePlayVideo}
                className={cn(
                  "absolute inset-0 flex items-center justify-center transition-opacity",
                  isPlaying ? "opacity-0 hover:opacity-100" : "opacity-100 bg-black/30"
                )}
              >
                <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
                  {isPlaying ? (
                    <Pause className="w-8 h-8 text-gray-900" />
                  ) : (
                    <Play className="w-8 h-8 text-gray-900 ml-1" />
                  )}
                </div>
              </button>
            </div>
          )}

          {introType === "voice" && introContentUrl && (
            <div className="p-8 rounded-xl bg-gradient-to-br from-violet-500/10 to-purple-500/10 border border-violet-500/20">
              <audio
                ref={audioRef}
                src={introContentUrl}
                onEnded={handleAudioEnded}
              />
              
              <div className="flex flex-col items-center gap-4">
                <button
                  onClick={togglePlayAudio}
                  className="w-20 h-20 rounded-full bg-violet-500 flex items-center justify-center shadow-lg hover:bg-violet-600 transition-colors"
                >
                  {isPlaying ? (
                    <Pause className="w-10 h-10 text-white" />
                  ) : (
                    <Play className="w-10 h-10 text-white ml-1" />
                  )}
                </button>
                <p className="text-sm text-muted-foreground">
                  {isPlaying ? "Playing..." : "Tap to play"}
                </p>
              </div>
            </div>
          )}

          {introType === "text" && introText && (
            <div className="p-6 rounded-xl bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-500/20">
              <Type className="w-8 h-8 text-blue-500 mb-4" />
              <p className="text-lg leading-relaxed">"{introText}"</p>
            </div>
          )}
        </div>

        <div className="flex justify-end">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default IntroCapsuleViewer;
